import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { Loan, LoanEvent } from '@/types';
import { EVENT_TYPE_LABELS } from '@/types';
import { summarizeLoanState, describeEstimateBasis, getLoanStatusLabel } from '@/lib/calc/loanState';
import { computeSavingsBaselines, computeSavingsSummary } from '@/lib/calc/savingsBaselines';
import { groupByFinancialYear, type FinancialYearGroup } from '@/features/schedule/financialYearGrouping';
import { summarizeEvent, eventDateLabel } from '@/features/events/eventDisplay';
import { formatDate, formatDateTime } from '@/lib/format';
import { isAfterOrEqual, todayIso } from '@/lib/calc/dates';

/**
 * ============================================================================
 * REPORT PDF GENERATION
 * ============================================================================
 *
 * Builds a formal, shareable PDF snapshot of the loan as of a selected
 * date. This file contains NO calculation logic of its own — every
 * figure comes from computeSchedule() / computeSavingsBaselines() /
 * computeSavingsSummary() / groupByFinancialYear(), the exact same
 * functions Home and Schedule already call. This file is purely PDF
 * layout: positioning text, drawing table rows, paginating.
 *
 * Uses pdf-lib (MIT), which runs entirely client-side in the browser —
 * no server, no native dependencies — consistent with this app's
 * local-first architecture.
 *
 * Standard PDF fonts (Helvetica) have no ₹ glyph, so amounts in the PDF
 * use a plain "Rs." prefix instead of formatInr()'s "₹" — a rendering
 * substitution only, not a new formatting/calculation utility. The
 * underlying numeric formatting (Indian digit grouping) is unaffected.
 */

const PAGE_WIDTH = 595.28; // A4 at 72dpi
const PAGE_HEIGHT = 841.89;
const MARGIN = 50;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const inrGroupingOnly = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const inrGroupingPrecise = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Rs.-prefixed amount formatter for PDF rendering (Helvetica has no ₹ glyph). See file header note. */
function pdfInr(amount: number, precise = false): string {
  return `Rs. ${precise ? inrGroupingPrecise.format(amount) : inrGroupingOnly.format(amount)}`;
}

/**
 * summarizeEvent()/eventDateLabel() (reused as-is from eventDisplay.ts —
 * see file header) build strings using characters outside the standard
 * PDF fonts' WinAnsi character set: "₹" (Indian Rupee Sign, via
 * formatInr()) and "→" (rightward arrow, via consequenceSuffix() on any
 * event carrying a repaymentConsequence — rate changes, prepayments,
 * balance corrections). Standard fonts throw a hard "WinAnsi cannot
 * encode" error on either character rather than silently dropping it,
 * so any event with a repayment consequence would previously crash PDF
 * generation whenever it fell within the selected as-of range (which is
 * why this only surfaced for a future as-of date whose range happened
 * to include such an event). This is a pure text substitution on the
 * already-produced string for PDF display only — it does not call
 * formatInr()/consequenceSuffix() again or reformat anything, so no
 * calculation/formatting logic is duplicated.
 */
function sanitizeForPdf(text: string): string {
  return text.replace(/₹/g, 'Rs. ').replace(/→/g, '->');
}

interface ReportContext {
  doc: PDFDocument;
  page: PDFPage;
  y: number;
  font: PDFFont;
  fontBold: PDFFont;
}

const COLOR_TEXT = rgb(0.1, 0.1, 0.12);
const COLOR_MUTED = rgb(0.42, 0.42, 0.46);
const COLOR_RULE = rgb(0.82, 0.82, 0.85);
const COLOR_HEADING = rgb(0.05, 0.05, 0.08);

function newPage(ctx: ReportContext) {
  ctx.page = ctx.doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  ctx.y = PAGE_HEIGHT - MARGIN;
}

/** Ensures at least `needed` points remain before the bottom margin, starting a new page otherwise. */
function ensureSpace(ctx: ReportContext, needed: number) {
  if (ctx.y - needed < MARGIN) {
    newPage(ctx);
  }
}

function drawSectionHeading(ctx: ReportContext, text: string) {
  ensureSpace(ctx, 34);
  ctx.y -= 6;
  ctx.page.drawText(text, { x: MARGIN, y: ctx.y, size: 13, font: ctx.fontBold, color: COLOR_HEADING });
  ctx.y -= 6;
  ctx.page.drawLine({
    start: { x: MARGIN, y: ctx.y },
    end: { x: PAGE_WIDTH - MARGIN, y: ctx.y },
    thickness: 0.75,
    color: COLOR_RULE,
  });
  ctx.y -= 16;
}

/** A label/value row, e.g. "Sanctioned Amount .......... Rs. 85,00,000". */
function drawRow(ctx: ReportContext, label: string, value: string, opts?: { muted?: boolean }) {
  ensureSpace(ctx, 16);
  ctx.page.drawText(label, { x: MARGIN, y: ctx.y, size: 10, font: ctx.font, color: opts?.muted ? COLOR_MUTED : COLOR_TEXT });
  const valueWidth = ctx.fontBold.widthOfTextAtSize(value, 10);
  ctx.page.drawText(value, {
    x: PAGE_WIDTH - MARGIN - valueWidth,
    y: ctx.y,
    size: 10,
    font: ctx.fontBold,
    color: COLOR_TEXT,
  });
  ctx.y -= 15;
}

function drawParagraph(ctx: ReportContext, text: string, opts?: { size?: number; muted?: boolean }) {
  const size = opts?.size ?? 9;
  const maxWidth = CONTENT_WIDTH;
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.font.widthOfTextAtSize(candidate, size) > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);

  for (const l of lines) {
    ensureSpace(ctx, 13);
    ctx.page.drawText(l, { x: MARGIN, y: ctx.y, size, font: ctx.font, color: opts?.muted ? COLOR_MUTED : COLOR_TEXT });
    ctx.y -= 13;
  }
}

function drawSpacer(ctx: ReportContext, amount = 10) {
  ctx.y -= amount;
}

/** Simple N-column table with a header row and left/right-aligned body columns. Used for the FY summary. */
function drawTable(
  ctx: ReportContext,
  columns: { label: string; width: number; align?: 'left' | 'right' }[],
  rows: string[][],
  opts?: { boldLastRow?: boolean }
) {
  const rowHeight = 16;
  const headerHeight = 18;

  function drawHeader() {
    ensureSpace(ctx, headerHeight + rowHeight);
    let x = MARGIN;
    for (const col of columns) {
      const textWidth = ctx.fontBold.widthOfTextAtSize(col.label, 8);
      const textX = col.align === 'right' ? x + col.width - textWidth : x;
      ctx.page.drawText(col.label, { x: textX, y: ctx.y, size: 8, font: ctx.fontBold, color: COLOR_MUTED });
      x += col.width;
    }
    ctx.y -= 6;
    ctx.page.drawLine({
      start: { x: MARGIN, y: ctx.y },
      end: { x: PAGE_WIDTH - MARGIN, y: ctx.y },
      thickness: 0.5,
      color: COLOR_RULE,
    });
    ctx.y -= 12;
  }

  drawHeader();

  rows.forEach((row, rowIndex) => {
    const isLast = opts?.boldLastRow && rowIndex === rows.length - 1;
    if (ctx.y - rowHeight < MARGIN) {
      newPage(ctx);
      drawHeader();
    }
    if (isLast) {
      ctx.y -= 4;
      ctx.page.drawLine({
        start: { x: MARGIN, y: ctx.y + 10 },
        end: { x: PAGE_WIDTH - MARGIN, y: ctx.y + 10 },
        thickness: 0.5,
        color: COLOR_RULE,
      });
    }
    let x = MARGIN;
    const font = isLast ? ctx.fontBold : ctx.font;
    row.forEach((cell, i) => {
      const col = columns[i];
      const size = 8;
      const textWidth = font.widthOfTextAtSize(cell, size);
      const textX = col.align === 'right' ? x + col.width - textWidth : x;
      ctx.page.drawText(cell, { x: textX, y: ctx.y, size, font, color: COLOR_TEXT });
      x += col.width;
    });
    ctx.y -= rowHeight;
  });
}

export interface GenerateReportPdfOptions {
  loan: Loan;
  events: LoanEvent[];
  asOfDate: string;
}

/**
 * Builds the full Report PDF and returns it as a Uint8Array, ready to be
 * wrapped in a Blob and downloaded. See file header for the section list
 * and data sources — every figure is produced by the existing
 * calculation engine / savings-baseline / FY-grouping functions.
 */
export async function generateReportPdf({ loan, events, asOfDate }: GenerateReportPdfOptions): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const ctx: ReportContext = { doc, page: doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]), y: PAGE_HEIGHT - MARGIN, font, fontBold };

  const isFutureDate = asOfDate > todayIso();

  // The same four calculation calls Home/Schedule already make.
  const loanState = summarizeLoanState({ loan, events, asOfDate });
  const currentPosition = loanState.position;
  const currentProjection = loanState.projection;
  const baselines = computeSavingsBaselines(loan, events);
  const savings = computeSavingsSummary(baselines);

  const totalDisbursed = events
    .filter((e): e is Extract<LoanEvent, { type: 'disbursement' }> => e.type === 'disbursement' && !e.supersededAt)
    .reduce((sum, e) => sum + e.amount, 0);

  const remainingTenureMonths = loanState.remainingRegularInstalments ?? 0;

  // ---- Header ----
  ctx.page.drawText('LIC Housing Finance — Home Loan Report', { x: MARGIN, y: ctx.y, size: 18, font: fontBold, color: COLOR_HEADING });
  ctx.y -= 22;
  ctx.page.drawText(`As of: ${formatDate(asOfDate)}${isFutureDate ? '  (Projected)' : ''}`, {
    x: MARGIN,
    y: ctx.y,
    size: 11,
    font,
    color: COLOR_MUTED,
  });
  ctx.y -= 15;
  ctx.page.drawText(`Generated on: ${formatDateTime(new Date().toISOString())}`, {
    x: MARGIN,
    y: ctx.y,
    size: 9,
    font,
    color: COLOR_MUTED,
  });
  ctx.y -= 10;
  ctx.page.drawLine({ start: { x: MARGIN, y: ctx.y }, end: { x: PAGE_WIDTH - MARGIN, y: ctx.y }, thickness: 1, color: COLOR_RULE });
  ctx.y -= 22;

  // ---- A. Loan Details ----
  drawSectionHeading(ctx, 'A. Loan Details');
  drawRow(ctx, 'Lender', sanitizeForPdf(loan.lender));
  if (loan.loanAccountNumber) drawRow(ctx, 'Loan Account Number', sanitizeForPdf(loan.loanAccountNumber));
  drawRow(ctx, 'Sanctioned Amount', pdfInr(loan.sanctionAmount));
  drawRow(ctx, 'Approved Amount', pdfInr(loan.approvedAmount));
  drawRow(ctx, 'Original Interest Rate', `${loan.originalAnnualRatePercent.toFixed(2)}% p.a.`);
  drawRow(ctx, 'Original Tenure', `${loan.originalTenureMonths} months`);
  drawRow(ctx, 'Original EMI (Full Sanctioned Amount)', pdfInr(loan.originalEmiAtFullDisbursement, true));
  drawRow(ctx, 'Loan Start Date', formatDate(loan.loanStartDate));
  drawRow(ctx, 'Regular EMI Start Date', formatDate(loan.regularEmiStartDate));
  drawRow(ctx, 'Loan Status', getLoanStatusLabel(loan, events).label);
  if (loan.lenderReported?.instalmentAmount !== undefined) {
    drawRow(ctx, 'LIC Reported Instalment', pdfInr(loan.lenderReported.instalmentAmount, true));
  }
  if (loanState.contractualMaturity) {
    drawRow(ctx, 'Contractual Maturity', formatDate(loanState.contractualMaturity.date));
  }
  drawSpacer(ctx, 16);

  // ---- B. Loan Position as of [date] ----
  drawSectionHeading(ctx, `B. Loan Position as of ${formatDate(asOfDate)}${isFutureDate ? ' (Projected)' : ''}`);
  drawRow(ctx, 'Total Disbursed', pdfInr(totalDisbursed, true));
  drawRow(
    ctx,
    isFutureDate ? 'Outstanding Principal (Projected)' : 'Outstanding Principal',
    pdfInr(isFutureDate ? currentPosition.summary.currentOutstandingPrincipal : loanState.recordedPrincipal, true)
  );
  drawRow(ctx, isFutureDate ? 'Principal Repaid (Projected)' : 'Principal Repaid', pdfInr(currentPosition.summary.totalPrincipalRepaid, true));
  drawRow(ctx, isFutureDate ? 'Prepayments (Projected)' : 'Prepayments', pdfInr(currentPosition.summary.totalPrepayments, true));
  drawRow(
    ctx,
    isFutureDate ? 'Applicable Interest Rate (Projected)' : 'Applicable Interest Rate',
    `${currentPosition.summary.currentApplicableAnnualRatePercent.toFixed(2)}% p.a.`
  );
  drawRow(
    ctx,
    `${isFutureDate ? 'EMI (Projected)' : 'Current EMI'}${loanState.regularEmi?.basis === 'derived_over_contract_tenure' ? ' (est.)' : ''}`,
    currentPosition.summary.isLoanClosed ? '—' : pdfInr(currentPosition.summary.currentApplicableEmi, true)
  );
  drawRow(ctx, 'Remaining Tenure', currentPosition.summary.isLoanClosed ? '0 months' : `${remainingTenureMonths} months`);
  drawRow(
    ctx,
    'Loan Status as of This Date',
    currentPosition.summary.isLoanClosed ? 'Closed' : isFutureDate ? 'Projected — Ongoing' : 'Ongoing'
  );
  drawSpacer(ctx, 16);

  // ---- C. Financial Impact ----
  drawSectionHeading(ctx, 'C. Financial Impact');
  const totalPaymentA = baselines.baselineA.summary.totalEmiPaid + baselines.baselineA.summary.totalPrepayments;
  const totalPaymentC = baselines.baselineC.summary.totalEmiPaid + baselines.baselineC.summary.totalPrepayments;
  drawRow(ctx, 'Original Projected Total Interest', pdfInr(baselines.baselineA.summary.totalInterestPaid, true));
  drawRow(ctx, 'Current Projected Total Interest', pdfInr(baselines.baselineC.summary.totalInterestPaid, true));
  drawRow(ctx, 'Interest Saved vs. Original', pdfInr(savings.interestSavedVsOriginal, true));
  drawRow(ctx, 'Original Projected Total Payment', pdfInr(totalPaymentA, true));
  drawRow(ctx, 'Current Projected Total Payment', pdfInr(totalPaymentC, true));
  drawRow(ctx, 'Total Payment Saved vs. Original', pdfInr(savings.totalPaymentSavedVsOriginal, true));
  drawRow(ctx, 'Time Saved vs. Original', `${savings.timeSavedVsOriginalMonths} months`);
  drawRow(ctx, 'Interest Impact of Rate Movement Alone', pdfInr(savings.interestSavedDueToRateMovementAlone, true));
  drawSpacer(ctx, 6);
  drawParagraph(
    ctx,
    'Figures above compare the loan\'s actual disbursement history against three baselines: the original sanctioned terms with no rate changes or prepayments; the original terms with actual rate changes but no prepayments (isolating rate movement alone); and the actual, complete event history.',
    { muted: true }
  );
  const basis = describeEstimateBasis(loanState.projectionInfo, { inr: (n, precise) => pdfInr(n, precise), date: (d) => formatDate(d) });
  if (basis) drawParagraph(ctx, sanitizeForPdf(basis), { muted: true });
  drawSpacer(ctx, 16);

  // ---- D. Repayment Summary by Financial Year ----
  drawSectionHeading(ctx, 'D. Repayment Summary by Financial Year');
  const fyGroups = groupByFinancialYear(currentProjection.months);
  if (fyGroups.length === 0) {
    drawParagraph(ctx, 'No repayment cycles have been generated yet.', { muted: true });
  } else {
    const columns = [
      { label: 'FY', width: 48, align: 'left' as const },
      { label: 'Opening Bal.', width: 78, align: 'right' as const },
      { label: 'EMI/Paid', width: 68, align: 'right' as const },
      { label: 'Interest', width: 68, align: 'right' as const },
      { label: 'Principal', width: 68, align: 'right' as const },
      { label: 'Prepaymt.', width: 68, align: 'right' as const },
      { label: 'Closing Bal.', width: 72, align: 'right' as const },
    ];
    const rows = fyGroups.map((fy) => fyRowToTableRow(fy));
    rows.push(lifetimeTotalRow(fyGroups));
    drawTable(ctx, columns, rows, { boldLastRow: true });
  }
  drawSpacer(ctx, 16);

  // ---- E. Loan Event History ----
  drawSectionHeading(ctx, 'E. Loan Event History');
  const eventsUpToAsOf = events
    .filter((e) => isAfterOrEqual(asOfDate, e.effectiveDate))
    .sort((a, b) => (a.effectiveDate < b.effectiveDate ? 1 : a.effectiveDate > b.effectiveDate ? -1 : 0));

  if (eventsUpToAsOf.length === 0) {
    drawParagraph(ctx, 'No events recorded up to this date.', { muted: true });
  } else {
    for (const event of eventsUpToAsOf) {
      ensureSpace(ctx, 28);
      ctx.page.drawText(`${eventDateLabel(event)} — ${EVENT_TYPE_LABELS[event.type]}`, {
        x: MARGIN,
        y: ctx.y,
        size: 8.5,
        font: fontBold,
        color: COLOR_MUTED,
      });
      ctx.y -= 12;
      drawParagraph(ctx, sanitizeForPdf(summarizeEvent(event)), { size: 9.5 });
      ctx.y -= 4;
    }
  }

  return doc.save();
}

function fyRowToTableRow(fy: FinancialYearGroup): string[] {
  return [
    fy.financialYear,
    pdfInr(fy.openingBalance),
    pdfInr(fy.totalEmiPaid),
    pdfInr(fy.totalInterest),
    pdfInr(fy.totalPrincipalViaEmi),
    pdfInr(fy.totalPrepayment),
    pdfInr(fy.closingBalance),
  ];
}

function lifetimeTotalRow(groups: FinancialYearGroup[]): string[] {
  const totalEmi = groups.reduce((sum, g) => sum + g.totalEmiPaid, 0);
  const totalInterest = groups.reduce((sum, g) => sum + g.totalInterest, 0);
  const totalPrincipal = groups.reduce((sum, g) => sum + g.totalPrincipalViaEmi, 0);
  const totalPrepayment = groups.reduce((sum, g) => sum + g.totalPrepayment, 0);
  return [
    'Lifetime',
    pdfInr(groups[0].openingBalance),
    pdfInr(totalEmi),
    pdfInr(totalInterest),
    pdfInr(totalPrincipal),
    pdfInr(totalPrepayment),
    pdfInr(groups[groups.length - 1].closingBalance),
  ];
}
